import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Input,
  Textarea,
  Alert,
  AlertIcon,
  Progress,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';
import { useToast } from '../hooks/useToast';
import fetchWithAuth from '../utils/fetchWithAuth';

const EMPTY_RECIPIENT = { email: '', surveyLink: '', firstName: '', sending: false, sent: false };

function EmailSendQueue({ pollId, onSendComplete }) {
  const [summary, setSummary] = useState(null);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [savedSubject, setSavedSubject] = useState('');
  const [savedBody, setSavedBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [batchRunning, setBatchRunning] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [testRecipients, setTestRecipients] = useState([{ ...EMPTY_RECIPIENT }]);

  const toast = useToast();
  const boxBgColor = useColorModeValue('background.shade2');
  const borderColor = useColorModeValue('gray.300');

  const fetchEmailQueue = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchWithAuth(`/api/polls/${pollId}/email-queue/`);
      if (response.ok) {
        const data = await response.json();
        setSummary(data.summary || null);
        setDryRun(data.dry_run);
        if (data.template) {
          setEmailSubject(data.template.email_subject || '');
          setEmailBody(data.template.email_body || '');
          setSavedSubject(data.template.email_subject || '');
          setSavedBody(data.template.email_body || '');
        }
      }
    } catch (err) {
      console.error('Failed to fetch email queue:', err);
    } finally {
      setLoading(false);
    }
  }, [pollId]);

  const checkBatchStatus = useCallback(async () => {
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/email-batch/status/`);
      if (response.ok) {
        const data = await response.json();
        setBatchRunning(data.running);
        return data.running;
      }
    } catch (err) {
      console.error('Failed to check email batch status:', err);
    }
    return false;
  }, [pollId]);

  useEffect(() => {
    fetchEmailQueue();
    checkBatchStatus();
  }, [fetchEmailQueue, checkBatchStatus]);

  // Auto-refresh while batch is running
  useEffect(() => {
    if (!batchRunning) return;
    const interval = setInterval(async () => {
      await fetchEmailQueue();
      const stillRunning = await checkBatchStatus();
      if (!stillRunning) {
        toast.showSuccess('Email Batch Complete', 'Server-side email batch has finished.');
        if (onSendComplete) onSendComplete();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [batchRunning, fetchEmailQueue, checkBatchStatus, onSendComplete, toast]);

  const handleSaveTemplate = async () => {
    setSaving(true);
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/email-template/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email_subject: emailSubject,
          email_body: emailBody,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setSavedSubject(data.email_subject);
        setSavedBody(data.email_body);
        toast.showSuccess('Saved', 'Email template saved.');
      } else {
        toast.showError('Error', 'Failed to save template.');
      }
    } catch (err) {
      toast.showError('Error', err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStartBatch = async () => {
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/email-batch/start/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ delay: 0.6 }),
      });
      const data = await response.json();
      if (response.ok && data.started) {
        setBatchRunning(true);
        toast.showSuccess('Email Batch Started',
          `Sending ${data.pending_count} emails on server (${data.delay}s delay). You can close this page.`);
      } else {
        toast.showError('Error', data.error || 'Failed to start email batch');
      }
    } catch (err) {
      toast.showError('Error', err.message);
    }
  };

  const handleStopBatch = async () => {
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/email-batch/stop/`, {
        method: 'POST',
      });
      if (response.ok) {
        toast.showSuccess('Stopping', 'Email batch will stop after the current message.');
      }
    } catch (err) {
      toast.showError('Error', err.message);
    }
  };

  const updateRecipient = (index, field, value) => {
    setTestRecipients(prev => prev.map((r, i) =>
      i === index ? { ...r, [field]: value } : r
    ));
  };

  const addRecipient = () => {
    if (testRecipients.length < 5) {
      setTestRecipients(prev => [...prev, { ...EMPTY_RECIPIENT }]);
    }
  };

  const removeRecipient = (index) => {
    if (testRecipients.length > 1) {
      setTestRecipients(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSendTestEmail = async (index) => {
    const recipient = testRecipients[index];
    if (!recipient.email) {
      toast.showError('Error', 'Email address is required');
      return;
    }
    updateRecipient(index, 'sending', true);
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/send-test-email/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to_address: recipient.email,
          survey_link: recipient.surveyLink,
          first_name: recipient.firstName,
        }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        updateRecipient(index, 'sent', true);
        toast.showSuccess('Sent', `Email sent to ${recipient.email}`);
      } else {
        toast.showError('Failed', data.error || 'Failed to send email');
      }
    } catch (err) {
      toast.showError('Error', err.message);
    } finally {
      updateRecipient(index, 'sending', false);
    }
  };

  const hasUnsavedChanges = emailSubject !== savedSubject || emailBody !== savedBody;
  const progressPct = summary
    ? (summary.sent / Math.max(summary.sent + summary.pending + summary.failed, 1)) * 100
    : 0;

  return (
    <Box>
      {/* Dry-run / live indicator */}
      {dryRun && (
        <Alert status="info" mb={4} borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            <strong>DRY RUN MODE</strong> -- Emails will be simulated, not actually sent.
          </Text>
        </Alert>
      )}
      {!dryRun && (
        <Alert status="warning" mb={4} borderRadius="md">
          <AlertIcon />
          <Text fontSize="sm">
            <strong>LIVE MODE</strong> -- Emails will be sent via Mailgun.
          </Text>
        </Alert>
      )}

      {/* Email template editor */}
      <Box bg={boxBgColor} p={5} borderRadius="md" border="1px solid" borderColor={borderColor} mb={4}>
        <HStack justify="space-between" mb={3}>
          <Heading size="sm">Email Template</Heading>
          <Button
            size="sm"
            colorScheme="blue"
            onClick={handleSaveTemplate}
            isLoading={saving}
            isDisabled={!hasUnsavedChanges}
          >
            {hasUnsavedChanges ? 'Save Template' : 'Saved'}
          </Button>
        </HStack>

        <VStack align="stretch" spacing={3}>
          <Box>
            <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Subject</Text>
            <Input
              size="sm"
              value={emailSubject}
              onChange={e => setEmailSubject(e.target.value)}
              placeholder="Email subject line"
            />
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="600" color="gray.500" mb={1}>Body</Text>
            <Textarea
              size="sm"
              value={emailBody}
              onChange={e => setEmailBody(e.target.value)}
              placeholder="Email body text"
              rows={10}
              fontFamily="mono"
              fontSize="xs"
            />
          </Box>
          <Text fontSize="xs" color="gray.500">
            Available variables: <Badge variant="subtle" fontSize="xs">{'{first_name}'}</Badge>{' '}
            <Badge variant="subtle" fontSize="xs">{'{survey_link}'}</Badge>{' '}
            <Badge variant="subtle" fontSize="xs">{'{unsubscribe_link}'}</Badge>
          </Text>
        </VStack>
      </Box>

      {/* Test Send */}
      <Box bg={boxBgColor} p={5} borderRadius="md" border="1px solid" borderColor={borderColor} mb={4}>
        <HStack justify="space-between" mb={3}>
          <Heading size="sm">Send Test Emails</Heading>
          {testRecipients.length < 5 && (
            <Button size="xs" variant="outline" onClick={addRecipient}>
              + Add recipient
            </Button>
          )}
        </HStack>

        <VStack align="stretch" spacing={2}>
          {testRecipients.map((recipient, index) => (
            <HStack key={index} spacing={2}>
              <Input
                size="sm"
                placeholder="Email address"
                value={recipient.email}
                onChange={e => updateRecipient(index, 'email', e.target.value)}
                flex={2}
                isDisabled={recipient.sent}
              />
              <Input
                size="sm"
                placeholder="Survey link (optional)"
                value={recipient.surveyLink}
                onChange={e => updateRecipient(index, 'surveyLink', e.target.value)}
                flex={2}
                isDisabled={recipient.sent}
              />
              <Input
                size="sm"
                placeholder="First name"
                value={recipient.firstName}
                onChange={e => updateRecipient(index, 'firstName', e.target.value)}
                flex={1}
                isDisabled={recipient.sent}
              />
              <Button
                size="sm"
                colorScheme={recipient.sent ? 'green' : 'blue'}
                onClick={() => handleSendTestEmail(index)}
                isLoading={recipient.sending}
                isDisabled={!recipient.email || recipient.sent}
                minW="70px"
              >
                {recipient.sent ? 'Sent' : 'Send'}
              </Button>
              {testRecipients.length > 1 && (
                <Button
                  size="sm"
                  variant="ghost"
                  color="gray.400"
                  onClick={() => removeRecipient(index)}
                  isDisabled={recipient.sending}
                >
                  x
                </Button>
              )}
            </HStack>
          ))}
        </VStack>
        <Text fontSize="xs" color="gray.400" mt={2}>
          Uses the saved email template above. Up to 5 recipients.
        </Text>
      </Box>

      {/* Summary + Send controls */}
      {summary && (
        <Box bg={boxBgColor} p={5} borderRadius="md" border="1px solid" borderColor={borderColor} mb={4}>
          <HStack justify="space-between" mb={3}>
            <VStack align="start" spacing={1}>
              <Heading size="sm">Email Queue</Heading>
              <HStack spacing={4}>
                <Text fontSize="sm" color="gray.600">
                  {summary.sent} sent
                </Text>
                <Text fontSize="sm" color="yellow.600" fontWeight="600">
                  {summary.pending} pending
                </Text>
                {summary.failed > 0 && (
                  <Text fontSize="sm" color="red.500">
                    {summary.failed} failed
                  </Text>
                )}
                <Text fontSize="sm" color="gray.500">
                  {summary.total_with_email} with email
                </Text>
              </HStack>
            </VStack>
            <HStack spacing={2}>
              {!batchRunning ? (
                <Button
                  colorScheme="green"
                  size="md"
                  onClick={handleStartBatch}
                  isDisabled={!summary.pending || batchRunning}
                >
                  Send All ({summary.pending})
                </Button>
              ) : (
                <Button
                  colorScheme="red"
                  size="md"
                  onClick={handleStopBatch}
                >
                  Stop
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={fetchEmailQueue} isLoading={loading}>
                Refresh
              </Button>
            </HStack>
          </HStack>

          {batchRunning && (
            <Text fontSize="xs" color="green.500" fontWeight="600" mb={2}>
              Sending on server (0.6s delay) -- auto-refreshing every 5s
            </Text>
          )}

          <Progress
            value={progressPct}
            colorScheme="blue"
            size="sm"
            borderRadius="md"
            mb={2}
            hasStripe={batchRunning}
            isAnimated={batchRunning}
          />
          <Text fontSize="xs" color="gray.500" textAlign="right">
            {summary.sent} / {summary.sent + summary.pending + summary.failed} sendable
          </Text>
        </Box>
      )}
    </Box>
  );
}

export default EmailSendQueue;
